import type {
    ClosedFishGroupRow,
    LocationType,
    OverviewCage,
    OverviewCageStatus,
} from '@/domain/client';

/** Which bucket a row came from. Drives the row background and the filtering. */
export type LocationOverviewRowStatus = OverviewCageStatus | 'closedFishGroup';

/**
 * A row is either a cage or a departed cohort — a discriminated union rather than
 * a cage with empty data.
 *
 * Synthesising a blank `OverviewCage` for a cohort row would have been shorter,
 * and wrong: the cage-link cell would offer to open a pen as if the cohort were
 * still in it, and the historical cell would offer to mark that pen historical
 * from a row that is not a pen. The second is destructive, not just misleading.
 */
export type LocationOverviewRow =
    | { status: Exclude<LocationOverviewRowStatus, 'closedFishGroup'>; cage: OverviewCage }
    | { status: 'closedFishGroup'; closedFishGroup: ClosedFishGroupRow };

/** Narrows a row to the cage kind, for the code paths that only apply to pens. */
export function isCageRow(
    row: LocationOverviewRow
): row is Extract<LocationOverviewRow, { cage: OverviewCage }> {
    return row.status !== 'closedFishGroup';
}

export type LocationOverviewColumnKey =
    | 'cage'
    | 'name'
    | 'fishGroup'
    | 'weight'
    | 'growth'
    | 'lice'
    | 'laksvel'
    | 'wound'
    | 'speed'
    | 'measurement'
    | 'historical';

/** Everything the column set depends on: licenses, feature flags, location kind. */
export interface LocationOverviewColumnContext {
    locationType: LocationType;
    hasAccessToLice: boolean;
    hasAccessToFishSpeed: boolean;
    hasAccessToBehavior: boolean;
    showBetaLaksvelColumn: boolean;
    showWoundColumn: boolean;
    showMarkHistorical: boolean;
    /**
     * Whether to show which cohort occupies each pen. Driven by the
     * `fish-group-tracking` flag, which also decides whether the overview request
     * asks for the data at all — so the column never appears empty by accident.
     *
     * It also removes the `Name` column and moves the pen name onto the link
     * button. Under this model the pen is static and the cohort is what the row
     * is about, so the button says where and this column says what — and the two
     * are one switch, because a pen name in both places is a pen name twice.
     */
    showFishGroupColumn: boolean;
}

export type LocationOverviewColumnVisibility = Record<LocationOverviewColumnKey, boolean>;
